import { useMemo, useRef } from "react";
import {
	useForm,
	useSelector,
	type StandardSchemaV1Issue,
} from "@tanstack/react-form";

import { Modal } from "@/primitives/Modal";
import { Form } from "@/primitives/Form";
import { Field } from "@/primitives/Field";
import { Input } from "@/primitives/Input";
import { Button } from "@/primitives/Button";
import { Select } from "@/primitives/Select";
import { FA6Icon } from "@/primitives/FA6Icon";
import { PasswordStrengthMeter } from "@/primitives/PasswordStrengthMeter";
import { PasswordRequirements } from "@/primitives/PasswordRequirements";
import { usePasswordFeedback } from "@/features/auth/usePasswordFeedback";
import type { PasswordStrengthValidation } from "@/features/auth/passwordStrengthValidation";
import { useModal } from "@/shared/reactIslandModal";
import { api } from "@/shared/api";
import type { ErrorResponse } from "@/shared/timr";
import { Notify } from "@/shared/ui/notify";
import {
	createChangePasswordSchema,
	type ChangePasswordFormValues,
} from "./changePasswordSchema";

export interface ChangePasswordModalProps {
	/** Whether the current user has connected mobile devices. Gates the disconnect select. */
	hasConnectedMobileDevices: boolean;
	/** Active client password policy, or undefined when the client enforces none. */
	passwordStrengthValidation?: PasswordStrengthValidation;
}

// Annotated (not inferred) so the form data type keeps `disconnectMobileDevices` at
// `boolean | null` — under `strictNullChecks: false` a bare `null` literal widens to `any`.
const defaultValues: ChangePasswordFormValues = {
	current: "",
	password: "",
	confirm: "",
	disconnectMobileDevices: null,
};

export function ChangePasswordModal({
	hasConnectedMobileDevices,
	passwordStrengthValidation,
}: ChangePasswordModalProps) {
	const { close } = useModal();

	const schema = useMemo(
		() =>
			createChangePasswordSchema(
				hasConnectedMobileDevices,
				passwordStrengthValidation,
			),
		[hasConnectedMobileDevices, passwordStrengthValidation],
	);

	// null = "Bitte wählen", selectable like in the legacy <select>; the schema rejects it,
	// so submitting without a real choice surfaces the "No option selected" error.
	const disconnectMobileDevicesItems = useMemo(
		() => [
			{
				label: messages["dialog.confirm.disconnectDevices.default"],
				value: null,
			},
			{
				label: messages["dialog.confirm.disconnectDevices.yes"],
				value: true,
			},
			{
				label: messages["dialog.confirm.disconnectDevices.no"],
				value: false,
			},
		],
		[],
	);

	// Opening the select popup moves focus off the trigger, so a plain trigger-onBlur would
	// mark the field blurred (and surface the required error) while the user is still choosing.
	// Blur therefore only counts when the trigger is left while closed, or when the popup closes.
	const disconnectSelectOpen = useRef(false);

	const form = useForm({
		defaultValues,
		validators: {
			onMount: schema,
			onChange: schema,
			// The request runs as async submit validation: the server's per-field messages
			// (HTTP 400 `{ fieldErrors }`) are returned straight back, keyed by field name.
			onSubmitAsync: async ({ value }) => {
				try {
					const { mobileDevicesDisconnected } = await api(
						"changePassword.json",
					).post({
						current: value.current,
						password: value.password,
						confirm: value.confirm,
						disconnectMobileDevices: hasConnectedMobileDevices
							? value.disconnectMobileDevices
							: false,
					});
					Notify.success(
						`changePassword.changed${mobileDevicesDisconnected ? ".devicesDisconnected" : ""}`,
					);
					close();
				} catch (response: unknown) {
					const fieldErrors = (response as ErrorResponse).responseJSON
						?.fieldErrors;
					if (fieldErrors != null) {
						return {
							fields: Object.fromEntries(
								fieldErrors.map((e) => [e.field, e.message]),
							),
						};
					}
				}
			},
		},
	});

	const password = useSelector(form.store, (state) => state.values.password);
	const submitted = useSelector(
		form.store,
		(state) => state.submissionAttempts > 0,
	);

	const { strength, requirements } = usePasswordFeedback(
		password,
		passwordStrengthValidation,
	);

	// "when to show an error": only after the field has been blurred (or a submit was attempted) — never while the user is still typing.
	// TanStack types `meta.errors` as `unknown[]`, and two shapes land in there: Standard Schema
	// issues (`{message}`) from the Zod validators, and the server's per-field message strings
	// returned by `onSubmitAsync`.
	const errorFor = (meta: {
		isBlurred: boolean;
		errors: readonly unknown[];
	}): string | null => {
		if (!meta.isBlurred && !submitted) return null;
		const error = meta.errors[0];
		if (typeof error === "string") return error;
		const message = (error as StandardSchemaV1Issue)?.message;
		return typeof message === "string" ? message : null;
	};

	return (
		<Modal.Content>
			<Modal.Header>
				<Modal.Title>{messages["changePassword.title"]}</Modal.Title>
			</Modal.Header>

			<Form
				orientation="horizontal"
				noValidate /* block native form validation */
				/*
				 * Capture phase, because Base UI's Form only forwards `onSubmit` while it
				 * considers every Field valid — with `Field.Root invalid` driven from the
				 * displayed errors it would swallow each retry and only focus the first
				 * invalid control. stopPropagation keeps its handler out entirely, so
				 * TanStack owns validation and submission.
				 */
				onSubmitCapture={(event) => {
					event.preventDefault();
					event.stopPropagation();
					void form.handleSubmit();
				}}
			>
				<Modal.Body>
					{/* Present only so password managers save the new password for the right account; hidden per the Chromium "amazing password forms" guidance. */}
					<input
						type="hidden"
						name="username"
						autoComplete="username"
						defaultValue={globals.userLogin}
					/>
					<form.Field name="current">
						{(field) => {
							const error = errorFor(field.state.meta);
							return (
								<Field.Root
									name="current"
									invalid={error != null}
								>
									<Field.Label>
										{messages["oldpassword.label"]}
									</Field.Label>
									<Input
										type="password"
										autoComplete="current-password"
										autoFocus
										value={field.state.value}
										onChange={(event) =>
											field.handleChange(
												event.target.value,
											)
										}
										onBlur={field.handleBlur}
									/>
									<Field.Error match={error != null}>
										{error}
									</Field.Error>
								</Field.Root>
							);
						}}
					</form.Field>

					<form.Field name="password">
						{(field) => {
							const error = errorFor(field.state.meta);
							return (
								<Field.Root
									name="password"
									invalid={error != null}
								>
									<Field.Label>
										{messages["newpassword1.label"]}
									</Field.Label>
									<Input
										type="password"
										autoComplete="new-password"
										value={field.state.value}
										onChange={(event) =>
											field.handleChange(
												event.target.value,
											)
										}
										onBlur={field.handleBlur}
									/>
									<PasswordStrengthMeter
										className="mt-8"
										value={strength.order}
										max={strength.maxOrder}
										level={strength.level}
										label={strength.label}
										aria-label={
											messages["passwordStrength.label"]
										}
									/>
									{requirements.length > 0 && (
										<PasswordRequirements
											requirements={requirements}
										/>
									)}
									<Field.Error match={error != null}>
										{error}
									</Field.Error>
								</Field.Root>
							);
						}}
					</form.Field>

					<form.Field name="confirm">
						{(field) => {
							const error = errorFor(field.state.meta);
							return (
								<Field.Root
									name="confirm"
									invalid={error != null}
								>
									<Field.Label>
										{messages["newpassword2.label"]}
									</Field.Label>
									<Input
										type="password"
										autoComplete="new-password"
										value={field.state.value}
										onChange={(event) =>
											field.handleChange(
												event.target.value,
											)
										}
										onBlur={field.handleBlur}
									/>
									<Field.Error match={error != null}>
										{error}
									</Field.Error>
								</Field.Root>
							);
						}}
					</form.Field>

					{hasConnectedMobileDevices && (
						<form.Field name="disconnectMobileDevices">
							{(field) => {
								const error = errorFor(field.state.meta);
								return (
									<Field.Root
										name="disconnectMobileDevices"
										invalid={error != null}
									>
										<Field.Label>
											{
												messages[
													"dialog.confirm.disconnectDevices.label"
												]
											}
										</Field.Label>
										<Select.Root
											items={disconnectMobileDevicesItems}
											value={field.state.value}
											onValueChange={(value) =>
												field.handleChange(value)
											}
											onOpenChange={(open) => {
												disconnectSelectOpen.current =
													open;
												if (!open) field.handleBlur();
											}}
										>
											<Select.Trigger
												onBlur={() => {
													if (
														!disconnectSelectOpen.current
													)
														field.handleBlur();
												}}
											/>
											<Select.Items />
										</Select.Root>
										<Field.Error match={error != null}>
											{error}
										</Field.Error>
									</Field.Root>
								);
							}}
						</form.Field>
					)}
				</Modal.Body>

				<Modal.Footer>
					<form.Subscribe
						selector={(state) => state.isSubmitting}
					>
						{(isSubmitting) => (
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting && (
									<FA6Icon
										icon="spinner-third"
										variant="regular"
										className="spinner mr-5"
									/>
								)}
								{messages["changePassword.change"]}
							</Button>
						)}
					</form.Subscribe>
					<Modal.Close
						render={<Button variant="secondary" type="button" />}
					>
						{messages["dialog.cancel"]}
					</Modal.Close>
				</Modal.Footer>
			</Form>
		</Modal.Content>
	);
}
